import React, { useState, useEffect } from "react";
import { Modal, Form, Select, DatePicker, Radio, Button, message } from "antd";
import { GlobalOutlined, EnvironmentOutlined, CalendarOutlined } from "@ant-design/icons";
import { Country, State, City } from "country-state-city";
import { useDispatch } from "react-redux";
import { updateMe } from "../../redux/slice/authSlice";
import dayjs from "dayjs";

const ProfileCompletionModal = () => {
  const [form] = Form.useForm();
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [missingFields, setMissingFields] = useState([]);
  const dispatch = useDispatch();

  useEffect(() => {
    checkProfileCompletion();
    setCountries(Country.getAllCountries());
  }, []);

  const checkProfileCompletion = () => {
    try {
      const storedUser = localStorage.getItem("user");
      if (!storedUser) return;

      let user = JSON.parse(storedUser);

      // Handle both formats: direct user object or nested { data: { user } }
      if (user?.data?.user) {
        user = user.data.user;
      } else if (user?.data) {
        user = user.data;
      }

      const fieldsToCheck = ["gender", "dob", "city", "state", "country"];
      const missing = fieldsToCheck.filter(
        (field) => !user[field] || user[field] === "" || user[field] === null
      );

      if (missing.length > 0) {
        setMissingFields(missing);
        setVisible(true);
      }
    } catch (error) {
      console.error("Error checking profile completion:", error);
    }
  };

  const handleCountryChange = (countryCode) => {
    setSelectedCountry(countryCode);
    const countryStates = State.getStatesOfCountry(countryCode);
    setStates(countryStates);
    setCities([]);
    setSelectedState(null);
    form.setFieldsValue({ state: undefined, city: undefined });
  };

  const handleStateChange = (stateCode) => {
    setSelectedState(stateCode);
    const stateCities = City.getCitiesOfState(selectedCountry, stateCode);
    setCities(stateCities);
    form.setFieldsValue({ city: undefined });
  };

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const countryObj = countries.find((c) => c.isoCode === values.country);
      const stateObj = states.find((s) => s.isoCode === values.state);
      const cityObj = cities.find((c) => c.name === values.city);

      const updatePayload = {};

      if (values.gender) updatePayload.gender = values.gender;
      if (values.dob) updatePayload.dob = values.dob.format("YYYY-MM-DD");
      if (values.city) updatePayload.city = cityObj?.name || values.city;
      if (values.state) updatePayload.state = stateObj?.name || values.state;
      if (values.country) {
        updatePayload.country = countryObj?.name || values.country;
        updatePayload.countryCode = countryObj?.isoCode || values.country;
        updatePayload.currency = countryObj?.currency || "INR";
        updatePayload.phoneCode = countryObj?.phonecode || "";
        updatePayload.latitude = parseFloat(countryObj?.latitude) || 0;
        updatePayload.longitude = parseFloat(countryObj?.longitude) || 0;
      }

      const result = await dispatch(updateMe(updatePayload));

      if (updateMe.fulfilled.match(result)) {
        // Update localStorage with new data
        const storedUser = localStorage.getItem("user");
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          if (userData?.data?.user) {
            userData.data.user = { ...userData.data.user, ...updatePayload };
          } else if (userData?.data) {
            userData.data = { ...userData.data, ...updatePayload };
          } else {
            Object.assign(userData, updatePayload);
          }
          localStorage.setItem("user", JSON.stringify(userData));
        }

        message.success("Profile updated successfully!");
        setVisible(false);
      } else {
        message.error("Failed to update profile. Please try again.");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      message.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const isMissing = (field) => missingFields.includes(field);

  return (
    <Modal
      title="Complete Your Profile"
      open={visible}
      closable={false}
      maskClosable={false}
      footer={null}
      centered
      width={520}
    >
    

      <Form form={form} layout="vertical" onFinish={onFinish}>
        {isMissing("gender") && (
          <Form.Item
            name="gender"
            label="Gender"
            rules={[{ required: true, message: "Please select your gender" }]}
          >
            <Radio.Group>
              <Radio value="male">Male</Radio>
              <Radio value="female">Female</Radio>
              <Radio value="other">Other</Radio>
            </Radio.Group>
          </Form.Item>
        )}

        {isMissing("dob") && (
          <Form.Item
            name="dob"
            label="Date of Birth"
            rules={[
              { required: true, message: "Please select your date of birth" },
            ]}
          >
            <DatePicker
              placeholder="Select Date of Birth"
              size="large"
              format="DD/MM/YYYY"
              suffixIcon={<CalendarOutlined />}
              style={{ width: "100%" }}
              disabledDate={(current) => current && current > dayjs().endOf("day")}
            />
          </Form.Item>
        )}

        {isMissing("country") && (
          <Form.Item
            name="country"
            label="Country"
            rules={[{ required: true, message: "Please select your country" }]}
          >
            <Select
              showSearch
              placeholder="Select Country"
              size="large"
              optionFilterProp="label"
              onChange={handleCountryChange}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              suffixIcon={<GlobalOutlined />}
              options={countries.map((country) => ({
                value: country.isoCode,
                label: `${country.flag} ${country.name}`,
              }))}
            />
          </Form.Item>
        )}

        {isMissing("state") && (
          <Form.Item
            name="state"
            label="State"
            rules={[{ required: true, message: "Please select your state" }]}
          >
            <Select
              showSearch
              placeholder="Select State"
              size="large"
              optionFilterProp="label"
              onChange={handleStateChange}
              disabled={!selectedCountry && isMissing("country")}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              suffixIcon={<EnvironmentOutlined />}
              options={states.map((state) => ({
                value: state.isoCode,
                label: state.name,
              }))}
            />
          </Form.Item>
        )}

        {isMissing("city") && (
          <Form.Item
            name="city"
            label="City"
            rules={[{ required: true, message: "Please select your city" }]}
          >
            <Select
              showSearch
              placeholder="Select City"
              size="large"
              optionFilterProp="label"
              disabled={!selectedState && isMissing("state")}
              filterOption={(input, option) =>
                (option?.label ?? "")
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              suffixIcon={<EnvironmentOutlined />}
              options={cities.map((city) => ({
                value: city.name,
                label: city.name,
              }))}
            />
          </Form.Item>
        )}

        <Form.Item style={{ marginBottom: 0, marginTop: 16 }}>
          <Button
            type="primary"
            htmlType="submit"
            size="large"
            loading={loading}
            block
            style={{
              background: "linear-gradient(97deg, #f96e40, #b61e66, #53286b)",
              border: "none",
              color: "#fff",
              height: 45,
              fontWeight: 600,
              fontSize: 16,
            }}
          >
            Save & Continue
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ProfileCompletionModal;
